import React, { useState } from 'react'; import {
  ChevronDown,
  ChevronRight,
  Download,
  Mail,
  Trash2,
  FileText,
  FilePlus,
  Bell,
  Key,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import Button from '../common/Button';

const BulkContractsList = ({
  bulkEntries = [],
  expandedEntries = [],
  onToggleExpand,
  onDownload,
  onSendEmails,
  onDelete,
  onCreateAdditionalActs,
  onCreateNotices,
  onDownloadBulkAdditionalActs,
  onSendBulkAdditionalActEmails,
  onDownloadBulkNotices,
  onSendBulkNoticeEmails,
  onGeneratePdfs,
  onGenerateBulkAdditionalActPdfs,
  onGenerateBulkNoticePdfs,
  loading = false
}) => {
  if (bulkEntries.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Nu există bulk entries
        </h3>
        <p className="text-gray-500">
          Creează primul bulk de contracte pentru a începe
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {bulkEntries.map((entry) => {
        const isExpanded = expandedEntries.includes(entry.id);

        const contractsWithoutPdf = entry.contracts?.filter(c => !c.pdfFilePath).length || 0;
        const allContractsHavePdf = contractsWithoutPdf === 0;

        return (
          <div
            key={entry.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1">
                  <button
                    onClick={() => onToggleExpand(entry.id)}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-600" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-600" />
                    )}
                  </button>

                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-base font-semibold text-gray-900">
                        Bulk Entry #{entry.id}
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {entry.totalContracts} contracte
                      </span>

                      {contractsWithoutPdf > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          {contractsWithoutPdf} fără PDF
                        </span>
                      )}
                    </div>

                    {entry.description && (
                      <p className="text-sm text-gray-600 mt-1">
                        {entry.description}
                      </p>
                    )}

                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span>
                        Creat: {format(new Date(entry.createdAt), 'dd MMM yyyy, HH:mm', { locale: ro })}
                      </span>
                      <span>•</span>
                      <span>
                        De: {entry.createdBy}
                      </span>
                      {entry.bulkAdditionalActs?.length > 0 && (
                        <>
                          <span>•</span>
                          <span className="flex items-center">
                            <FilePlus className="h-3 w-3 mr-1" />
                            {entry.bulkAdditionalActs.length} acte adiționale
                          </span>
                        </>
                      )}
                      {entry.bulkNotices?.length > 0 && (
                        <>
                          <span>•</span>
                          <span className="flex items-center">
                            <Bell className="h-3 w-3 mr-1" />
                            {entry.bulkNotices.length} notificări
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <Button
                    variant={allContractsHavePdf ? "outline" : "primary"}
                    size="sm"
                    icon={RefreshCw}
                    onClick={() => onGeneratePdfs(entry.id)}
                    title={allContractsHavePdf ? "Regenerează toate PDF-urile" : "Generează PDF-uri lipsă"}
                  >
                    {allContractsHavePdf ? 'Regenerează PDF' : 'Generează PDF'}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    icon={Download}
                    onClick={() => onDownload(entry.id)}
                    title="Descarcă toate contractele ca ZIP"
                  >
                    Descarcă
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    icon={Mail}
                    onClick={() => {
                      console.log(entry.id);
                      onSendEmails(entry.id)}}
                    title="Trimite toate contractele prin email"
                  >
                    Trimite
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    icon={FilePlus}
                    onClick={() => onCreateAdditionalActs(entry)}
                    title="Creează acte adiționale pentru toate contractele"
                  >
                    Acte Adiționale
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    icon={Bell}
                    onClick={() => onCreateNotices(entry)}
                    title="Creează notificări de reziliere pentru toate contractele"
                  >
                    Notificări
                  </Button>

                  <Button
                    variant="danger"
                    size="sm"
                    icon={Trash2}
                    onClick={() => onDelete(entry.id)}
                    title="Șterge bulk entry"
                  />
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="border-t border-gray-200 bg-gray-50">
                <BulkEntryDetails
                  entry={entry}
                  onDownloadBulkAdditionalActs={onDownloadBulkAdditionalActs}
                  onSendBulkAdditionalActEmails={onSendBulkAdditionalActEmails}
                  onDownloadBulkNotices={onDownloadBulkNotices}
                  onSendBulkNoticeEmails={onSendBulkNoticeEmails}
                  onGenerateBulkAdditionalActPdfs={onGenerateBulkAdditionalActPdfs}
                  onGenerateBulkNoticePdfs={onGenerateBulkNoticePdfs}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const BulkEntryDetails = ({
  entry,
  onDownloadBulkAdditionalActs,
  onSendBulkAdditionalActEmails,
  onDownloadBulkNotices,
  onSendBulkNoticeEmails,
  onGenerateBulkAdditionalActPdfs,
  onGenerateBulkNoticePdfs
}) => {
  const [expandedBulkActs, setExpandedBulkActs] = useState([]);
  const [expandedBulkNotices, setExpandedBulkNotices] = useState([]);

  const toggleBulkActExpand = (bulkActId) => {
    setExpandedBulkActs(prev =>
      prev.includes(bulkActId)
        ? prev.filter(id => id !== bulkActId)
        : [...prev, bulkActId]
    );
  };

  const toggleBulkNoticeExpand = (bulkNoticeId) => {
    setExpandedBulkNotices(prev =>
      prev.includes(bulkNoticeId)
        ? prev.filter(id => id !== bulkNoticeId)
        : [...prev, bulkNoticeId]
    );
  };

  return (
    <div className="p-4">
      {/* Contracts Table - EXISTING CODE */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">
          Contracte ({entry.contracts?.length || 0})
        </h4>

        {entry.contracts && entry.contracts.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Număr Contract
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Expirare
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valoare
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entry.contracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {contract.number}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {contract.clientName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                        {contract.statusText}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(contract.dueDate), 'dd MMM yyyy', { locale: ro })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {contract.totalAmount?.toLocaleString('ro-RO')} {contract.currency}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">

                      <a href={`/contracts/${contract.id}`}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Detalii
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Nu există contracte.</p>
        )}
      </div>

      {/* Bulk Additional Acts - UPDATED */}
      {entry.bulkAdditionalActs && entry.bulkAdditionalActs.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <FilePlus className="h-4 w-4 mr-2" />
            Acte Adiționale Bulk ({entry.bulkAdditionalActs.length})
          </h4>

          <div className="space-y-3">
            {entry.bulkAdditionalActs.map((bulkAct) => {
              const isExpanded = expandedBulkActs.includes(bulkAct.id);
              const actsWithoutPdf = bulkAct.additionalActs?.filter(act => !act.pdfFilePath).length || 0;
              const allActsHavePdf = actsWithoutPdf === 0;
              return (
                <div key={bulkAct.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* Bulk Act Header */}
                  <div className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <button
                          onClick={() => toggleBulkActExpand(bulkAct.id)}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-600" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-600" />
                          )}
                        </button>

                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900">
                              {bulkAct.numberPrefix} - {bulkAct.totalActs} acte
                            </p>
                            {actsWithoutPdf > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                {actsWithoutPdf} fără PDF
                              </span>
                            )}
                          </div>

                          <p className="text-xs text-gray-500 mt-1">
                            Nouă dată expirare: {format(new Date(bulkAct.newEndDate), 'dd MMM yyyy', { locale: ro })}
                          </p>
                          <p className="text-xs text-gray-500">
                            Creat: {format(new Date(bulkAct.createdAt), 'dd MMM yyyy, HH:mm', { locale: ro })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Button
                          variant={allActsHavePdf ? "outline" : "primary"}
                          size="sm"
                          icon={RefreshCw}
                          onClick={() => onGenerateBulkAdditionalActPdfs(bulkAct.id)}
                          title={allActsHavePdf ? "Regenerează toate PDF-urile" : "Generează PDF-uri lipsă"}
                        >
                          {allActsHavePdf ? 'Regenerează' : 'Generează PDF'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          icon={Download}
                          onClick={() => onDownloadBulkAdditionalActs(bulkAct.id)}
                          title="Descarcă toate actele adiționale ca ZIP"
                        >
                          Descarcă
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          icon={Mail}
                          onClick={() => onSendBulkAdditionalActEmails(bulkAct.id)}
                          title="Trimite toate actele adiționale prin email"
                        >
                          Trimite
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Additional Acts List */}
                  {isExpanded && bulkAct.additionalActs && bulkAct.additionalActs.length > 0 && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-white">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Număr Act
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Contract
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Acces
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Data
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Acțiuni
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {bulkAct.additionalActs.map((act) => (
                              <tr key={act.id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {act.number}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                  {act.contractNumber}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(act.statusText)}`}>
                                    {act.statusText}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {act.Contract?.hasAccessToken ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      <Key className="h-3 w-3 mr-1" />
                                      Generat
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                      Fără acces
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {format(new Date(act.date), 'dd MMM yyyy', { locale: ro })}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">

                                  <a href={`/contracts/${act.contractId}`}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Detalii
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bulk Notices - UPDATED */}
      {entry.bulkNotices && entry.bulkNotices.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <Bell className="h-4 w-4 mr-2" />
            Notificări Reziliere Bulk ({entry.bulkNotices.length})
          </h4>

          <div className="space-y-3">
            {entry.bulkNotices.map((bulkNotice) => {
              const isExpanded = expandedBulkNotices.includes(bulkNotice.id);
              const noticesWithoutPdf = bulkNotice.notices?.filter(notice => !notice.pdfFilePath).length || 0;
              const allNoticesHavePdf = noticesWithoutPdf === 0;
              return (
                <div key={bulkNotice.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* Bulk Notice Header */}
                  <div className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <button
                          onClick={() => toggleBulkNoticeExpand(bulkNotice.id)}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-600" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-600" />
                          )}
                        </button>

                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900">
                              {bulkNotice.totalNotices} notificări
                            </p>
                            {/* NEW: Badge for missing PDFs */}
                            {noticesWithoutPdf > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                {noticesWithoutPdf} fără PDF
                              </span>
                            )}
                          </div>
                          {bulkNotice.terminationArticle && (
                            <p className="text-xs text-gray-500 mt-1">
                              Articol: {bulkNotice.terminationArticle}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            Creat: {format(new Date(bulkNotice.createdAt), 'dd MMM yyyy, HH:mm', { locale: ro })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Button
                          variant={allNoticesHavePdf ? "outline" : "primary"}
                          size="sm"
                          icon={RefreshCw}
                          onClick={() => onGenerateBulkNoticePdfs(bulkNotice.id)}
                          title={allNoticesHavePdf ? "Regenerează toate PDF-urile" : "Generează PDF-uri lipsă"}
                        >
                          {allNoticesHavePdf ? 'Regenerează' : 'Generează PDF'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          icon={Download}
                          onClick={() => onDownloadBulkNotices(bulkNotice.id)}
                          title="Descarcă toate notificările ca ZIP"
                        >
                          Descarcă
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          icon={Mail}
                          onClick={() => onSendBulkNoticeEmails(bulkNotice.id)}
                          title="Trimite toate notificările prin email"
                        >
                          Trimite
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Notices List */}
                  {isExpanded && bulkNotice.notices && bulkNotice.notices.length > 0 && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-white">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Contract
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Data
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Articol
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Acțiuni
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {bulkNotice.notices.map((notice) => (
                              <tr key={notice.id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {notice.contractNumber}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(notice.statusText)}`}>
                                    {notice.statusText}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {format(new Date(notice.date), 'dd MMM yyyy', { locale: ro })}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {notice.terminationArticle || '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">

                                  <a href={`/contracts/${notice.contractId}`}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Detalii
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusColor = (status) => {
  const statusColors = {
    'Draft': 'bg-gray-100 text-gray-800',
    'Generated': 'bg-blue-100 text-blue-800',
    'Sent': 'bg-yellow-100 text-yellow-800',
    'Accessed': 'bg-orange-100 text-orange-800',
    'Signed': 'bg-green-100 text-green-800',
    'Expired': 'bg-red-100 text-red-800',
    'Terminated': 'bg-red-100 text-red-800'
  };

  return statusColors[status] || 'bg-gray-100 text-gray-800';
};

export default BulkContractsList;